import isNil from 'lodash.isnil'

type ObjectWithNoNullishValues<T extends Record<string, unknown>> = {
  [K in keyof T]: Exclude<T[K], null | undefined>
}

/**
 * Filters out nullish values from an object.
 *
 * @param obj - The object to filter.
 * @returns A new object with nullish values removed.
 */
export function objectFilterNullish<T extends Record<string, unknown>>(
  object: T
): ObjectWithNoNullishValues<T> {
  return Object.fromEntries(
    Object.entries(object).filter(([, value]) => !isNil(value))
  ) as ObjectWithNoNullishValues<T>
}

/**
 * Creates a deep copy of an object with all null values removed.
 *
 * This function converts the object to JSON and back, which has two effects:
 * 1. It creates a deep copy of the original object
 * 2. It replaces all null values with undefined, which are then omitted from the resulting object
 *
 * Note that this method will also convert JavaScript-specific values (like undefined,
 * Functions, and Symbols) to null or remove them as a side effect of the JSON serialization.
 *
 * @template T - The type of the input object
 * @param {T} object - The object to filter
 * @returns {T} A new object with all null values removed
 */
export function objectFilterNullishDeep<T extends Record<string, unknown>>(object: T) {
  return JSON.parse(JSON.stringify(object, (_, value) => (value === null ? undefined : value)))
}
